import * as React from "react";
import { observer } from "mobx-react";

import { useLocalization } from "@shared-ui/localization-context";
import { ViewMedium, Viewport, ViewSmall } from "@shared-ui/viewport-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";
import { UitkText } from "uitk-react-text";

import { MultiLOBPackageWizardContext } from "src/components/flexComponents/PageLevelProviders/WizardContextProvider/MultiLOBPackageWizardContext";

import { LobTitle } from "components/shared/LobTitle/LobTitle";
import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { Travelers } from "components/shared/Travelers/Travelers";

import { WizardHotelPWAFilter } from "components/flexComponents/WizardHotelPWA/components/WizardHotelPWAFilter";
import { LobSubtitle } from "components/shared/LobSubtitle/LobSubtitle";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";
import { locKeys } from "components/flexComponents/WizardHotelPWA/components/l10n";
import { isVariantEnabled, Experiment } from "components/utility/experiment";

import { HotelAddFlightCheckbox } from "../components/HotelAddFlightCheckbox";
import { HotelAddCarCheckbox } from "../components/HotelAddCarCheckbox";
import { HotelHiddenSearchData } from "../components/HotelHiddenSearchData";
import { MultiplePlaces } from "../components/MultiplePlaces";

import { NoRehydratationValues, TravelersTriggerTypes } from "components/shared/TravelersField/typings";
import { PackagesSubLobs } from "stores/wizard/state/typings";
import { WizardHotelPWAv2Props } from "../typings";
import { Columns } from "components/shared/StorefrontWizard/typings";

export const WizardHotelPWAv2View: React.FunctionComponent<WizardHotelPWAv2Props> = observer((props) => {
  const { formatText } = useLocalization();
  const {
    globalWizardState,
    hotelWizardState,
    hotelWizardState: { config, numberOfErrors, errorInputRef, moreThanOneError },
  } = props.wizardState;
  const { context } = props;
  const {
    elementId,
    enableAddFlight,
    enableAddCar,
    enableMultiplePlaces,
    desktopAutoOpenCalendar,
    showDestinationField,
  } = config;
  const { isBiasedWizardEmpty } = hotelWizardState.config;
  const displayTravelersInline = !isBiasedWizardEmpty
    ? true
    : isVariantEnabled(context, Experiment.Alignment_of_Traveler_Input);
  const { halfWizard } = globalWizardState.config;

  const locationColumns: Columns = {
    fullWidth: {
      small: 2,
      large: displayTravelersInline ? 7 : 5,
    },
    half: {
      small: 2,
      large: 12,
    },
  };
  const datesColumns: Columns = {
    fullWidth: {
      small: 2,
      large: displayTravelersInline ? 5 : 4,
    },
    half: {
      small: 2,
      large: 12,
    },
  };
  const travelersColumns: Columns = {
    fullWidth: {
      small: 2,
      large: 3,
    },
    half: {
      small: 2,
      large: 2,
    },
  };
  const addFlightColumn: Columns = {
    fullWidth: {
      small: 1,
      large: 2,
    },
    half: {
      small: 1,
      large: 4,
    },
  };
  const addCarColumn: Columns = {
    fullWidth: {
      small: 1,
      large: 2,
    },
    half: {
      small: 1,
      large: 4,
    },
  };
  const multiplePlacesColumn: Columns = {
    fullWidth: {
      small: 2,
      large: 3,
    },
    half: {
      small: 2,
      large: 12,
    },
  };
  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, large: 12 };
  const datesColSpan = datesColumns[halfWizard ? "half" : "fullWidth"];
  const locationColSpan = locationColumns[halfWizard ? "half" : "fullWidth"];
  const travelersColSpan: UitkLayoutConditionalGridSpan = travelersColumns[halfWizard ? "half" : "fullWidth"];
  const addFlightColSpan: UitkLayoutConditionalGridSpan = addFlightColumn[halfWizard ? "half" : "fullWidth"];
  const addCarColSpan: UitkLayoutConditionalGridSpan = addCarColumn[halfWizard ? "half" : "fullWidth"];
  const multiplePlacesColSpan: UitkLayoutConditionalGridSpan = multiplePlacesColumn[halfWizard ? "half" : "fullWidth"];
  const hotelFiltersColSpan: UitkLayoutConditionalGridSpan = { small: 2, large: 8 };

  const isMultiplePlacesDisabled = hotelWizardState.isAddACarChecked || hotelWizardState.isAddAFlightChecked;

  const disclaimerMultiplePlacesLocKey = isMultiplePlacesDisabled
    ? hotelWizardState.config.useDialogTextWithoutCar
      ? locKeys.dialogTextWithoutCar
      : locKeys.dialogText
    : "";

  /**
   * Values used if no rehydratation(JS disabled)
   */
  const noRehydratationValues: NoRehydratationValues = {
    numOfTravelers: 2,
    name: "adults",
  };

  const [autoOpenCalendar, setAutoOpenCalendar] = React.useState<boolean>(Boolean(desktopAutoOpenCalendar));

  const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
    hotelWizardState.wizardInputsArray.push(ref);
    if (secondRef) {
      hotelWizardState.wizardInputsArray.push(secondRef);
    }
  };

  const getLocError = (isLink: boolean) => {
    if (!isLink) {
      return hotelWizardState.moreThanOneError()
        ? config.form.submitButton.locMultipleWarningsToken
        : config.form.submitButton.locWarningToken;
    }

    return hotelWizardState.moreThanOneError()
      ? config.form.submitButton.locMultipleWarningsLinkToken
      : config.form.submitButton.locWarningLinkToken;
  };

  /**
   * When user add a flight to the current trip search,
   * a origin field is displayed so as a legend for multiple places (if enabled)
   * Fields below add a flight checkbox should be visible in mobile view
   * Following values are taken from UDS tokens:
   * http://uds.ch.lab.stockyard.io/expedia/tokens/#foundation-breakpoint
   */
  const onAddFlightCheckboxChange = () => {
    const VIEWPORT_MAX_SMALL_BREAKPOINT = 575;
    const SPACE_ABOVE_WIZARD_IN_PIXELS = 200;
    const SPACE_LEFT_WIZARD_IN_PIXELS = 0;
    const SCROLL_BEHAVIOR = "smooth";
    const { innerWidth: viewportSize } = window;

    if (viewportSize < VIEWPORT_MAX_SMALL_BREAKPOINT) {
      window.scrollTo({
        top: SPACE_ABOVE_WIZARD_IN_PIXELS,
        left: SPACE_LEFT_WIZARD_IN_PIXELS,
        behavior: SCROLL_BEHAVIOR,
      });
    }
  };

  const updatePackagesOnReflection = () => {
    const packageState = props.wizardState.packageWizardState;
    const { isAddAFlightChecked, isAddACarChecked } = hotelWizardState;

    if (isAddACarChecked && isAddAFlightChecked) {
      packageState.updateSubLOBState(PackagesSubLobs.FLIGHT_HOTEL_CAR);
    } else if (isAddACarChecked) {
      packageState.updateSubLOBState(PackagesSubLobs.HOTEL_CAR);
    } else if (isAddAFlightChecked) {
      packageState.updateSubLOBState(PackagesSubLobs.FLIGHT_HOTEL);
    }
  };

  const toggleDatePicker = () => {
    setAutoOpenCalendar(!autoOpenCalendar);
  };

  const getLayoutTravelersInline = (alignment: "end" | "start") => (
    <UitkLayoutFlex
      data-testid="travelers-inline-link"
      alignItems={alignment}
      justifyContent={alignment}
      space="six"
      className="all-l-padding-six"
    >
      <>
        <UitkLayoutFlexItem alignSelf={alignment}>
          <Travelers
            lobState={hotelWizardState}
            colSpan={travelersColSpan}
            noRehydratationValues={noRehydratationValues}
            triggerType={TravelersTriggerTypes.LINK_BUTTON}
          />
        </UitkLayoutFlexItem>
      </>
    </UitkLayoutFlex>
  );

  return (
    <form
      noValidate={true}
      className="WizardHotelPWAv2"
      id={elementId}
      data-fm={props.flexComponent.fmId}
      data-fm-title-id={props.flexComponent.fmTitleId}
      onSubmit={hotelWizardState.submit}
      action={hotelWizardState.formAction}
    >
      <MultiLOBPackageWizardContext.Consumer>
        {({ isInsideMultiLOBPackageWizard }) => {
          if (isInsideMultiLOBPackageWizard) {
            props.wizardState.hotelWizardState.toggleMultiplePlacesCheckbox({ stateToForce: false });
          }

          return (
            <>
              {hotelWizardState.config.heading.fixedTitle && (
                <LobTitle lobState={hotelWizardState} customTitle={hotelWizardState.config.heading.title} />
              )}
              <LobSubtitle lobState={hotelWizardState} />

              {displayTravelersInline && (
                <Viewport>
                  <ViewSmall>{getLayoutTravelersInline("start")}</ViewSmall>
                  <ViewMedium>{getLayoutTravelersInline("end")}</ViewMedium>
                </Viewport>
              )}

              <UitkLayoutGrid columns={mainGridColumns} space="three" className="all-t-margin-three">
                {numberOfErrors > 0 && (
                  <LobErrorSummary
                    locHeadingToken={getLocError(false)!}
                    locHeadingArgs={moreThanOneError() && [numberOfErrors]}
                    locLinkToken={getLocError(true)}
                    linkClickFocusId="roundtrip-error"
                    inputErrorRef={errorInputRef}
                    colSpan={mainGridColumns}
                    setInputsRefs={setInputsRefs}
                  />
                )}
                {showDestinationField && (
                  <Location
                    originField={false}
                    destinationField={true}
                    lobState={hotelWizardState}
                    colSpan={locationColSpan}
                    setInputsRefs={setInputsRefs}
                    globalConfig={globalWizardState.config}
                    onClick={props.destinationOnClick}
                  />
                )}
                <Dates
                  lobState={hotelWizardState}
                  colSpan={datesColSpan}
                  setInputsRefs={setInputsRefs}
                  autoOpenCalendar={autoOpenCalendar}
                  toggleDatePicker={toggleDatePicker}
                />

                {!displayTravelersInline && isBiasedWizardEmpty && (
                  <Travelers
                    lobState={hotelWizardState}
                    colSpan={travelersColSpan}
                    noRehydratationValues={noRehydratationValues}
                  />
                )}

                {enableMultiplePlaces && isMultiplePlacesDisabled && (
                  <UitkLayoutGridItem colSpan={mainGridColumns}>
                    <div>
                      <UitkText size={300}>{formatText(disclaimerMultiplePlacesLocKey)}</UitkText>
                    </div>
                  </UitkLayoutGridItem>
                )}

                {enableAddFlight && !isInsideMultiLOBPackageWizard && (
                  <UitkLayoutGridItem colSpan={addFlightColSpan}>
                    <div>
                      <HotelAddFlightCheckbox
                        hotelWizardState={hotelWizardState}
                        onChange={onAddFlightCheckboxChange}
                        updatePackageOnReflection={updatePackagesOnReflection}
                      />
                    </div>
                  </UitkLayoutGridItem>
                )}

                {enableAddCar && !isInsideMultiLOBPackageWizard && (
                  <UitkLayoutGridItem colSpan={addCarColSpan}>
                    <div>
                      <HotelAddCarCheckbox
                        hotelWizardState={hotelWizardState}
                        updatePackageOnReflection={updatePackagesOnReflection}
                      />
                    </div>
                  </UitkLayoutGridItem>
                )}

                {enableMultiplePlaces && (
                  <MultiplePlaces
                    hotelWizardState={hotelWizardState}
                    disabled={isMultiplePlacesDisabled}
                    isChecked={hotelWizardState.isMultiplePlacesChecked}
                    colSpan={multiplePlacesColSpan}
                  />
                )}
              </UitkLayoutGrid>

              {hotelWizardState.isAddAFlightChecked && (
                <UitkLayoutGrid columns={mainGridColumns}>
                  <UitkLayoutGridItem colSpan={locationColSpan}>
                    <div>
                      <Location originField={true} destinationField={false} lobState={hotelWizardState} />
                    </div>
                  </UitkLayoutGridItem>
                </UitkLayoutGrid>
              )}

              {hotelWizardState.config.includeFiltersHotelPWAWizard && (
                <UitkLayoutGrid columns={mainGridColumns}>
                  <UitkLayoutGridItem colSpan={hotelFiltersColSpan}>
                    <div>
                      <WizardHotelPWAFilter
                        changeFormProperties={hotelWizardState.changeFormFilterProperties}
                        checkboxFilterChecks={hotelWizardState.checkboxFilterChecks}
                        advancedOptionsFilterVisible={hotelWizardState.advancedOptionsFilterVisible}
                      />
                    </div>
                  </UitkLayoutGridItem>
                </UitkLayoutGrid>
              )}

              <SubmitRow lobState={hotelWizardState} rfrr="SearchClick_Hotel" globalConfig={globalWizardState.config} />

              <HotelHiddenSearchData hotelWizardState={hotelWizardState} />
            </>
          );
        }}
      </MultiLOBPackageWizardContext.Consumer>
    </form>
  );
});

export default WizardHotelPWAv2View;
